import { c as _c } from "react/compiler-runtime";
/**
 * Practitioner-related data fetchers
 */
import { QueryClient, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createOrUpdatePractitioner, updatePractitioner, deletePractitioner } from "@/src/actions/practitioner";
import { toast } from "@/src/hooks/use-toast";
import { REFERRALS_CACHE_KEY } from "@/src/lib/data-fetchers/referrals-fetchers";
import { TASKS_CACHE_KEY, TASK_CACHE_KEY } from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Practitioner } from "@/src/models/practitioner";
import { getAllPatientPractitioners, getAllPractitioners, getPractitionerById } from "@/src/requests/practitioner";
import { isNotFound } from "@/src/requests/utils/fetch-json";
export const PRACTITIONERS_CACHE_KEY = "practitioners";
const deDuplicatePractitioners = (practitioners: Practitioner[]) => {
  // First sort practitioners to prioritize those with NPI numbers
  const sortedPractitioners = [...practitioners].sort((a, b) => {
    if (a.npiNumber && !b.npiNumber) return -1;
    if (!a.npiNumber && b.npiNumber) return 1;
    return 0;
  });

  // Then deduplicate using name and role criteria
  const uniquePractitioners = [];
  const namesSet = new Set();
  for (const practitioner of sortedPractitioners) {
    const nameKey = `${practitioner.givenName}|${practitioner.familyName}|${practitioner.role}`;
    if (!namesSet.has(nameKey)) {
      namesSet.add(nameKey);
      uniquePractitioners.push(practitioner);
    }
  }
  return uniquePractitioners;
};

// Todo: add refetch interval
export function usePractitioners() {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: [PRACTITIONERS_CACHE_KEY],
      queryFn: getAllPractitioners,
      retry: false,
      staleTime: Infinity
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const query = useQuery(t0);
  let t1;
  if ($[1] !== query.data) {
    t1 = deDuplicatePractitioners(Object.values(query.data || {})).sort(_temp);
    $[1] = query.data;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const practitioners = t1;
  let t2;
  if ($[3] !== query.data) {
    t2 = query.data || {};
    $[3] = query.data;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== practitioners || $[6] !== query.error || $[7] !== query.isError || $[8] !== query.isFetching || $[9] !== query.isLoading || $[10] !== query.refetch || $[11] !== t2) {
    t3 = {
      practitionersMap: t2,
      practitioners,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      refetch: query.refetch
    };
    $[5] = practitioners;
    $[6] = query.error;
    $[7] = query.isError;
    $[8] = query.isFetching;
    $[9] = query.isLoading;
    $[10] = query.refetch;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
function _temp(a, b) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}
export function usePractitionerById(displayId, options) {
  const $ = _c(14);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== displayId) {
    t0 = [PRACTITIONERS_CACHE_KEY, displayId];
    $[0] = displayId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== displayId || $[3] !== queryClient) {
    t1 = async () => {
      const practitionersCache = queryClient.getQueryData([PRACTITIONERS_CACHE_KEY]);
      if (practitionersCache && practitionersCache[displayId]) {
        logger.info("Found practitioner in cache");
        return practitionersCache[displayId];
      }
      logger.info("Fetching practitioner from server");
      ;
      try {
        const practitioner = await getPractitionerById(displayId);
        if (practitioner) {
          updatePractitionersQueryData(queryClient, practitioner);
        }
        return practitioner;
      } catch (t2) {
        const err = t2;
        if (isNotFound(err as Error)) {
          return null;
        }
        throw err;
      }
    };
    $[2] = displayId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = options?.enabled !== false && !!displayId;
  let t3;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: Infinity,
      enabled: t2
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    data: practitioner_0,
    isFetching,
    isLoading,
    isError
  } = useQuery(t3);
  let t4;
  if ($[9] !== isError || $[10] !== isFetching || $[11] !== isLoading || $[12] !== practitioner_0) {
    t4 = {
      practitioner: practitioner_0,
      isFetching,
      isLoading,
      isError
    };
    $[9] = isError;
    $[10] = isFetching;
    $[11] = isLoading;
    $[12] = practitioner_0;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
export function usePatientPractitioners(patientId) {
  const $ = _c(19);
  let t0;
  let t1;
  if ($[0] !== patientId) {
    t0 = [PRACTITIONERS_CACHE_KEY, "patient", patientId];
    t1 = () => {
      if (!patientId) {
        return {} as Awaited<ReturnType<typeof getAllPatientPractitioners>>;
      }
      return getAllPatientPractitioners(patientId);
    };
    $[0] = patientId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(patientId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: Infinity,
      refetchInterval: 60000
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const query = useQuery(t3);
  let t4;
  if ($[7] !== query.data) {
    t4 = query.data || {};
    $[7] = query.data;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== query.data) {
    t5 = Object.values(query.data || {}).sort(_temp2);
    $[9] = query.data;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== query.error || $[12] !== query.isError || $[13] !== query.isFetching || $[14] !== query.isLoading || $[15] !== query.refetch || $[16] !== t4 || $[17] !== t5) {
    t6 = {
      patientPractitionersMap: t4,
      patientPractitioners: t5,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      refetch: query.refetch
    };
    $[11] = query.error;
    $[12] = query.isError;
    $[13] = query.isFetching;
    $[14] = query.isLoading;
    $[15] = query.refetch;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}
function _temp2(a, b) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}
export function usePractitionerUpdateMutation() {
  const $ = _c(7);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== queryClient) {
    t0 = async practitioner_0 => {
      await queryClient.cancelQueries({
        queryKey: [PRACTITIONERS_CACHE_KEY]
      });
      const previousPractitioners = queryClient.getQueryData([PRACTITIONERS_CACHE_KEY]) ?? {};
      const practitionersCache = queryClient.getQueryData([PRACTITIONERS_CACHE_KEY]) ?? {};
      if (practitionersCache[practitioner_0.id]) {
        updatePractitionersQueryData(queryClient, practitioner_0);
      }
      return {
        previousPractitioners
      };
    };
    t1 = (error, variables, context) => {
      logger.error("Error updating practitioner");
      if (context) {
        queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], context.previousPractitioners);
        if (variables.displayId && context.previousPractitioners[variables.displayId]) {
          queryClient.setQueryData([PRACTITIONERS_CACHE_KEY, variables.displayId], context.previousPractitioners[variables.displayId]);
        }
        if (variables.id && context.previousPractitioners[variables.id]) {
          queryClient.setQueryData([PRACTITIONERS_CACHE_KEY, variables.id], context.previousPractitioners[variables.id]);
        }
      }
      toast({
        title: "Failed to save practitioner",
        description: "There was an error saving the practitioner. Please try again.",
        variant: "destructive"
      });
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]]
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t0 || $[5] !== t1) {
    t3 = {
      mutationFn: _temp3,
      onMutate: t0,
      onError: t1,
      meta: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useMutation(t3);
}
async function _temp3(practitioner) {
  const result = await updatePractitioner(practitioner.id, practitioner);
  if (!result) {
    throw new Error("Failed to update practitioner");
  }
  return result;
}
export const updatePractitionersQueryData = (queryClient: QueryClient, updatedPractitioner: Practitioner) => {
  queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], (oldData: Record<string, Practitioner> = {}) => {
    return {
      ...oldData,
      [updatedPractitioner.id]: updatedPractitioner
    };
  });
  if (updatedPractitioner.displayId) {
    queryClient.setQueryData([PRACTITIONERS_CACHE_KEY, updatedPractitioner.displayId], updatedPractitioner);
  }
  if (updatedPractitioner.id) {
    queryClient.setQueryData([PRACTITIONERS_CACHE_KEY, updatedPractitioner.id], updatedPractitioner);
  }
};
export function usePractitionerCreateOrUpdateMutation() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  let t2;
  if ($[0] !== queryClient) {
    t0 = async practitioner_0 => {
      await queryClient.cancelQueries({
        queryKey: [PRACTITIONERS_CACHE_KEY]
      });
      const previousPractitioners = queryClient.getQueryData([PRACTITIONERS_CACHE_KEY]) ?? {};
      updatePractitionersQueryData(queryClient, practitioner_0);
      return {
        previousPractitioners
      };
    };
    t1 = savedPractitioner => {
      updatePractitionersQueryData(queryClient, savedPractitioner);
    };
    t2 = (error, _variables, context) => {
      logger.error("Error creating practitioner");
      if (context) {
        queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], context.previousPractitioners);
      }
      toast({
        title: "Failed to create practitioner",
        description: "There was an error creating the practitioner. Please try again.",
        variant: "destructive"
      });
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]]
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t4 = {
      mutationFn: _temp4,
      onMutate: t0,
      onSuccess: t1,
      onError: t2,
      meta: t3
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useMutation(t4);
}
async function _temp4(practitioner) {
  const result = await createOrUpdatePractitioner(practitioner);
  if (!result) {
    throw new Error("Failed to create practitioner");
  }
  return result;
}
export function useDeletePractitionerMutation() {
  const $ = _c(9);
  const queryClient = useQueryClient();
  let t0;
  let t1;
  let t2;
  if ($[0] !== queryClient) {
    t0 = async t3 => {
      const {
        practitionerId: practitionerId_0
      } = t3;
      await queryClient.cancelQueries({
        queryKey: [PRACTITIONERS_CACHE_KEY]
      });
      const previousPractitioners = queryClient.getQueryData([PRACTITIONERS_CACHE_KEY]) ?? {};
      queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], t4 => {
        const practitioners = t4 === undefined ? {} : t4;
        const updated = {
          ...practitioners
        };
        delete updated[practitionerId_0];
        return updated;
      });
      queryClient.removeQueries({
        queryKey: [PRACTITIONERS_CACHE_KEY, practitionerId_0]
      });
      return {
        previousPractitioners
      };
    };
    t1 = (error, t5, context) => {
      const {
        practitionerId: practitionerId_1
      } = t5;
      if (context) {
        queryClient.setQueryData([PRACTITIONERS_CACHE_KEY], context.previousPractitioners);
      }
      logger.error("Error deleting practitioner:", {
        practitionerId: practitionerId_1,
        error
      });
      toast({
        title: "Practitioner deletion failed",
        description: "There was an error deleting the practitioner. Please try again.",
        variant: "destructive"
      });
    };
    t2 = () => {
      queryClient.invalidateQueries({
        queryKey: [PRACTITIONERS_CACHE_KEY]
      });
      toast({
        title: "Practitioner deleted successfully"
      });
    };
    $[0] = queryClient;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      invalidates: [[REFERRALS_CACHE_KEY], [TASKS_CACHE_KEY], [TASK_CACHE_KEY]]
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t4 = {
      mutationFn: _temp5,
      onMutate: t0,
      onError: t1,
      onSuccess: t2,
      meta: t3
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useMutation(t4);
}
async function _temp5(t0) {
  const {
    practitionerId
  } = t0;
  return deletePractitioner({
    practitionerId
  });
}